import { Fragment, useEffect, useMemo, useState } from "react";
//--------------------------------------------------------------------------------------------
import Resize from "src/components/common/resize";
import { appTitle, getTimeStamp, myAxios, MyFormData } from "src/components/common/functions";
import { useMyStore } from "src/components/main/mainStore";
import AddInventoryModal from "./AddInventoryModal";
import CreateYardModal from "./CreateYardModal";
import InventoryHistoryModal from "./InventoryHistoryModal";
import MoveInventoryModal from "./MoveInventoryModal";
import { formatDateTime, formatQty } from "./inventoryUtils";
//--------------------------------------------------------------------------------------------
const title = "Inventory";
const emptyInventory = {
    yards: [],
    items: [],
    stock: [],
    recentLogs: [],
    users: [],
};
//--------------------------------------------------------------------------------------------
export default function Inventory({ tabTitle }){
    const user = useMyStore(state => state.user);
    const setShowLoader = useMyStore(state => state.setShowLoader);
    const [inventory, setInventory] = useState(emptyInventory);
    const [updateDateTime, setUpdateDateTime] = useState(getTimeStamp());
    const [showLoading, setShowLoading] = useState(false);
    const [filterWords, setFilterWords] = useState("");
    const [modal, setModal] = useState(null);
    const [selectedStockRow, setSelectedStockRow] = useState(null);
    const [stockMenuId, setStockMenuId] = useState(null);
    const [historyLogs, setHistoryLogs] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    //--------------------------------------------------------------------------------------------
    useEffect(() => {
        document.title = tabTitle || title;
        return () => { document.title = appTitle; };
    }, [tabTitle]);
    //--------------------------------------------------------------------------------------------
    useEffect(() => {
        let isMounted = true;
        const controller = new AbortController();
        const loadInventory = async() => {
            setShowLoading(true);
            const res = await myAxios("inventory/readInventory.php", undefined, undefined, controller.signal);
            if(!isMounted) return;
            setShowLoading(false);
            if(!res) return;
            setInventory(res.data.data || emptyInventory);
        };
        loadInventory();
        return () => {
            isMounted = false;
            controller.abort();
            setShowLoader(false);
        };
    }, [updateDateTime, setShowLoader]);
    //--------------------------------------------------------------------------------------------
    const yardMap = useMemo(() => Object.fromEntries((inventory.yards || []).map(yard => [String(yard.id), yard])), [inventory.yards]);
    const itemMap = useMemo(() => Object.fromEntries((inventory.items || []).map(item => [String(item.id), item])), [inventory.items]);
    const users = inventory.users || [];
    const stockByYard = useMemo(() => {
        const grouped = {};
        (inventory.yards || []).forEach(yard => { grouped[yard.id] = []; });
        (inventory.stock || []).forEach(row => {
            if(!grouped[row.yardId]) grouped[row.yardId] = [];
            grouped[row.yardId].push(row);
        });
        return grouped;
    }, [inventory.yards, inventory.stock]);
    const filteredYards = useMemo(() => {
        const words = filterWords.toLowerCase().split(" ").filter(Boolean);
        if(!words.length) return inventory.yards || [];
        return (inventory.yards || []).filter(yard => {
            const stockText = (stockByYard[yard.id] || []).map(row => `${row.itemName} ${row.quantity} ${row.unit || ""}`).join(" ");
            const haystack = `${yard.name} ${stockText}`.toLowerCase();
            return words.every(word => haystack.includes(word));
        });
    }, [filterWords, inventory.yards, stockByYard]);
    const lowStock = row => row.lowStockQty !== null && row.lowStockQty !== undefined && row.lowStockQty !== "" && Number(row.quantity) <= Number(row.lowStockQty);
    const refresh = () => setUpdateDateTime(getTimeStamp());
    const openMoveInventory = row => {
        setSelectedStockRow(row);
        setStockMenuId(null);
        setModal("move");
    };
    //--------------------------------------------------------------------------------------------
    const openHistory = async() => {
        setModal("history");
        setHistoryLoading(true);
        const formData = new MyFormData();
        formData.append("limit", "200");
        const res = await myAxios("inventory/readStockLog.php", formData);
        setHistoryLoading(false);
        if(!res) return;
        setHistoryLogs(res.data.logs || []);
    };
    //--------------------------------------------------------------------------------------------
    return(
        <Fragment>
        <div className = "cssTab inventoryTab">
            <div className = "cssHeader inventoryHeader" style = {{background: user.userTheme}}>
                <span>
                    <Resize/>
                    {title}
                    <span data-toggle = "tooltip" title = {"Refreshed on " + formatDateTime(updateDateTime)} className = "btn btn-primary py-1 ml-4" onClick = {refresh}>↻</span>
                </span>
                <span className = "inventorySearchWrap">
                    <input
                        className = "form-control inventorySearch"
                        type = "text"
                        placeholder = "Search yards or items"
                        value = {filterWords}
                        onChange = {e => setFilterWords(e.currentTarget.value)}
                    />
                </span>
                <span className = "inventoryHeaderActions">
                    <button type = "button" className = "btn btn-light btn-sm" onClick = {() => setModal("yard")}>Create Yard</button>
                    <button type = "button" className = "btn btn-light btn-sm" onClick = {() => setModal("add")}>Add Inventory</button>
                    <button type = "button" className = "btn btn-light btn-sm" onClick = {() => setModal("remove")}>Move Inventory</button>
                    <button type = "button" className = "btn btn-light btn-sm" onClick = {openHistory}>View Movement History</button>
                </span>
            </div>
            <div className = "cssContent pb10 inventoryContent">
                {showLoading?<div className = "cssLoading">Loading inventory...</div>:
                !(inventory.yards || []).length && !(inventory.stock || []).length?
                    <div className = "inventoryEmpty">
                        <h4>No inventory has been added yet.</h4>
                        <p>Create a yard first, then add inventory items to that yard.</p>
                        <button type = "button" className = "btn btn-primary" onClick = {() => setModal("yard")}>Create First Yard</button>
                    </div>:
                    <div className = "inventoryYardGrid">
                        {filteredYards.map(yard => {
                            const rows = stockByYard[yard.id] || [];
                            return <div className = "inventoryYardCard" key = {yard.id}>
                                <div className = "inventoryYardTitle">{yard.name}</div>
                                {!rows.length?
                                    <div className = "inventoryNoStock">No stock in this yard yet.</div>:
                                    <div className = "inventoryStockList">
                                        {rows.map(row => <div className = {`inventoryStockRow ${lowStock(row) ? "inventoryLowStock" : ""}`} key = {row.id}>
                                            <div>
                                                <div className = "inventoryItemName">{row.itemName}</div>
                                                <div className = "inventoryMeta">
                                                    {row.lastPoNumber?`PO ${row.lastPoNumber}`:"No PO on last add"}
                                                    {row.updatedAt?` • Updated ${formatDateTime(row.updatedAt)}`:""}
                                                </div>
                                            </div>
                                            <div className = "inventoryStockActions">
                                                <div className = "inventoryQtyBox">
                                                    <div className = "inventoryQty">{formatQty(row.quantity)}</div>
                                                    <div className = "inventoryUnit">{row.unit || "qty"}</div>
                                                    {lowStock(row)?<div className = "inventoryLowBadge">Low</div>:null}
                                                </div>
                                                <button
                                                    type = "button"
                                                    className = "inventoryContextButton"
                                                    aria-label = {`Open actions for ${row.itemName}`}
                                                    aria-expanded = {stockMenuId === row.id}
                                                    onClick = {() => setStockMenuId(stockMenuId === row.id ? null : row.id)}
                                                >⋮</button>
                                                {stockMenuId === row.id?
                                                    <div className = "inventoryContextMenu">
                                                        <button type = "button" onClick = {() => openMoveInventory(row)}>Move Inventory</button>
                                                    </div>
                                                :null}
                                            </div>
                                        </div>)}
                                    </div>}
                            </div>;
                        })}
                    </div>}
            </div>
        </div>
        {modal === "yard"?
            <CreateYardModal closeIt = {() => setModal(null)} onDone = {refresh}/>
        :null}
        {modal === "add"?
            <AddInventoryModal
                closeIt = {() => setModal(null)}
                onDone = {refresh}
                yards = {inventory.yards || []}
                items = {inventory.items || []}
                setShowLoader = {setShowLoader}
            />
        :null}
        {modal === "remove"?
            <MoveInventoryModal
                closeIt = {() => setModal(null)}
                onDone = {refresh}
                yards = {inventory.yards || []}
                stock = {inventory.stock || []}
                users = {users}
                yardMap = {yardMap}
                itemMap = {itemMap}
                setShowLoader = {setShowLoader}
            />
        :null}
        {modal === "move"?
            <MoveInventoryModal
                closeIt = {() => { setModal(null); setSelectedStockRow(null); }}
                onDone = {refresh}
                yards = {inventory.yards || []}
                stock = {inventory.stock || []}
                users = {users}
                yardMap = {yardMap}
                itemMap = {itemMap}
                setShowLoader = {setShowLoader}
                initialStockRow = {selectedStockRow}
                initialDestinationType = "yard"
                title = "Move Inventory"
            />
        :null}
        {modal === "history"?
            <InventoryHistoryModal
                closeIt = {() => setModal(null)}
                logs = {historyLogs.length ? historyLogs : inventory.recentLogs || []}
                loading = {historyLoading}
            />
        :null}
        </Fragment>
    );
}
